import React, { useMemo, useState, useEffect } from 'react';
import { Menu, Empty } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSidebarContext } from './SidebarContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAdmin } from '../../../contexts/AdminContext';
import { getMenuGroups } from './menuConfig';
import { matchSearch } from './SidebarSearch';
import { emptyContainerStyle } from './styles';

/**
 * 侧边栏菜单组件
 */
const SidebarMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed, searchKeyword, closeDrawer, isMobile } = useSidebarContext();
  useTheme(); // 确保主题上下文可用
  
  // 受控的展开状态
  const [openKeys, setOpenKeys] = useState([]);
  
  const { hasPermission } = useAdmin();
  const menuGroups = useMemo(() => getMenuGroups(), []);
  
  // 过滤菜单项
  const filteredMenuItems = useMemo(() => {
    return menuGroups.map(group => {
      const filteredItems = group.items.filter(item => {
        // 权限检查
        if (item.requiredPermissions && !item.requiredPermissions.some(p => hasPermission(p))) {
          return false;
        }

        // 搜索过滤
        if (!searchKeyword) return true;
        if (matchSearch(item.label, searchKeyword)) return true;
        if (item.children) {
          return item.children.some(child => matchSearch(child.label, searchKeyword));
        }
        return false;
      }).map(item => {
        // 如果有子菜单，也过滤子菜单
        if (item.children) {
          const filteredChildren = item.children.filter(child => {
            // 子菜单权限检查
            if (child.requiredPermissions && !child.requiredPermissions.some(p => hasPermission(p))) {
              return false;
            }
            // 子菜单搜索过滤
            if (!searchKeyword) return true;
            return matchSearch(child.label, searchKeyword) || matchSearch(item.label, searchKeyword);
          });

          return { ...item, children: filteredChildren };
        }
        return item;
      }).filter(item => {
        // 如果有子菜单但子菜单全部被过滤掉了，且自身也不匹配搜索（且不是主菜单强制显示），则移除该主菜单
        if (item.children && item.children.length === 0 && item.requiredPermissions) {
           return false;
        }
        return true;
      });
      
      return { ...group, items: filteredItems };
    }).filter(group => group.items.length > 0);
  }, [searchKeyword, hasPermission, menuGroups]);

  // 构建 Ant Design Menu items
  const menuItems = useMemo(() => {
    const stripCustomProps = (item) => {
      const { requiredPermissions, children, ...rest } = item;
      if (children) {
        return { ...rest, children: children.map(stripCustomProps) };
      }
      return rest;
    };

    if (collapsed) {
      // 折叠模式：扁平化显示，不显示分组
      return filteredMenuItems.flatMap(group => group.items.map(stripCustomProps));
    }
    
    // 展开模式：使用 type: 'group' 分组显示
    return filteredMenuItems.map(group => ({
      type: 'group',
      label: group.title,
      key: `group-${group.key}`,
      children: group.items.map(stripCustomProps),
    }));
  }, [filteredMenuItems, collapsed]);
  
  // 计算当前选中的 keys
  const selectedKeys = useMemo(() => {
    return [location.pathname];
  }, [location.pathname]);
  
  // 路由变化时自动展开对应的子菜单
  useEffect(() => {
    const keys = [];
    menuGroups.forEach(group => {
      group.items.forEach(item => {
        if (item.children) {
          const hasActiveChild = item.children.some(
            child => location.pathname === child.key || location.pathname.startsWith(child.key + '/')
          );
          if (hasActiveChild) {
            keys.push(item.key);
          }
        }
      });
    });
    // 合并现有的 openKeys，避免关闭用户手动展开的菜单
    setOpenKeys(prev => {
      const newKeys = [...new Set([...prev, ...keys])];
      return newKeys;
    });
  }, [location.pathname, menuGroups]);
  
  // 处理子菜单展开/折叠
  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };
  
  // 点击菜单项
  const handleMenuClick = ({ key }) => {
    navigate(key);
    // 移动端点击后关闭抽屉
    if (isMobile) {
      closeDrawer();
    }
  };
  
  // 菜单容器样式 - 关键：flex: 1 + overflow: auto
  const menuContainerStyle = {
    flex: 1,
    minHeight: 0, // 重要：允许 flex 子元素收缩
    overflow: 'auto',
    overflowX: 'hidden',
  };
  
  // 搜索无结果
  if (filteredMenuItems.length === 0) {
    return (
      <div style={emptyContainerStyle}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={'无匹配结果'}
        />
      </div>
    );
  }
  
  return (
    <div style={menuContainerStyle}>
      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        openKeys={collapsed ? [] : openKeys}
        onOpenChange={handleOpenChange}
        items={menuItems}
        onClick={handleMenuClick}
        inlineCollapsed={collapsed}
        style={{ 
          borderRight: 0,
          background: 'transparent',
        }}
      />
    </div>
  );
};

export default SidebarMenu;
